/* /employees — port of prototype pgEmployees/filteredEmps/thSort (server-side filter/sort/paging). */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAllEmployees, useEmployees, useToday } from "../../data/hooks";
import type { Employee, EmployeeQuery } from "../../data/types";
import { nitaqat } from "../../domain/compliance";
import { daysTo } from "../../domain/dates";
import { useI18n, LOOKUP } from "../../i18n";
import { csvOf, downloadText } from "../../lib/format";
import { useSession } from "../../app/session";
import { useOverlays } from "../../app/overlays";
import { Button, Chip, DualDate, EmptyState, ErrorBox, ExpiryChip, Icon, Money, PageHeader, Person, SearchBox, Select, Skeleton, useToast } from "../../ui";
import AiImportModal from "./components/AiImportModal";
import { canWriteHr } from "./components/shared";

export const PER_PAGE = 12;
type SortKey = NonNullable<EmployeeQuery["sort"]>;

export function employeesCsv(emps: Employee[]): string {
  return csvOf([
    ["Employee ID", "Name (EN)", "Name (AR)", "Nationality", "ID/Iqama", "Department", "Branch", "Job title", "Joined", "Basic", "Housing", "Transport", "IBAN"],
    ...emps.map((e) => [e.code, e.nameEn, e.nameAr, LOOKUP.NAT[e.nationality]?.[0] ?? e.nationality, e.idNumber, LOOKUP.DEPT[e.dept]?.[0] ?? e.dept, LOOKUP.BR[e.branch]?.[0] ?? e.branch, e.title, e.joined, e.basic, e.housing, e.transport, e.iban.replace(/\s/g, "")]),
  ]);
}

export default function Employees() {
  const i = useI18n();
  const { t, L } = i;
  const { role } = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const { openPrint } = useOverlays();
  const today = useToday();
  const hr = canWriteHr(role);

  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [branch, setBranch] = useState("");
  const [nat, setNat] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<SortKey>("name");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [sel, setSel] = useState<number[]>([]);
  const [open, setOpen] = useState<number[]>([]);
  const [importing, setImporting] = useState(false);

  const query: EmployeeQuery = { q: q.trim() || undefined, dept: dept || undefined, branch: branch || undefined, nat: nat || undefined, status: status || undefined, sort, dir, page, pageSize: PER_PAGE };
  const listQ = useEmployees(query, { placeholderData: (prev) => prev });
  const allQ = useAllEmployees();

  const reset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };
  const clear = () => { setQ(""); setDept(""); setBranch(""); setNat(""); setStatus(""); setPage(1); };
  const filtered = !!(q || dept || branch || nat || status);
  const onSort = (k: SortKey) => { if (sort === k) setDir(dir === "asc" ? "desc" : "asc"); else { setSort(k); setDir("asc"); } setPage(1); };

  const exportCsv = () => {
    if (!allQ.data) return;
    downloadText("employees.csv", employeesCsv(allQ.data));
    toast(L("Employees exported", "تم تصدير الموظفين"));
  };

  const all = allQ.data ?? [];
  const act = all.filter((e) => e.status !== "Terminated");
  const pct = nitaqat(all).pct;
  const branches = new Set(act.map((e) => e.branch)).size;

  const th = (k: SortKey, label: string, cls?: string) => (
    <th className={cls} aria-sort={sort === k ? (dir === "asc" ? "ascending" : "descending") : undefined}>
      <button type="button" className="th-btn" onClick={() => onSort(k)} data-testid={"sort-" + k}>{label}<Icon name={sort === k ? (dir === "asc" ? "arrow-up" : "arrow-down") : "chevrons-up-down"} className="s" /></button>
    </th>
  );

  const toolbar = (
    <div className="toolbar" data-testid="emp-toolbar">
      <SearchBox value={q} onChange={reset(setQ)} placeholder={t("Search by name, ID or Iqama…")} testId="emp-search" />
      <Select aria-label={t("Department")} value={dept} onChange={(e) => reset(setDept)(e.target.value)} options={[["", t("All departments")], ...Object.keys(LOOKUP.DEPT).map((k) => [k, i.dept(k)] as [string, string])]} data-testid="emp-dept" />
      <Select aria-label={t("Branch")} value={branch} onChange={(e) => reset(setBranch)(e.target.value)} options={[["", t("All branches")], ...Object.keys(LOOKUP.BR).map((k) => [k, i.branch(k)] as [string, string])]} data-testid="emp-branch" />
      <Select aria-label={t("Nationality")} value={nat} onChange={(e) => reset(setNat)(e.target.value)} options={[["", t("All nationalities")], ["SA", t("Saudi")], ["non", t("Non-Saudi")]]} data-testid="emp-nat" />
      <Select aria-label={t("Status")} value={status} onChange={(e) => reset(setStatus)(e.target.value)} options={[["", t("All statuses")], ["Active", t("Active")], ["On leave", t("On leave")], ["Probation", t("Probation")], ["Terminated", t("Terminated")]]} data-testid="emp-status" />
      {filtered && <Button variant="ghost sm" onClick={clear} data-testid="emp-clear">{t("Clear")}</Button>}
    </div>
  );

  let table;
  if (listQ.error) table = <div className="card-b"><ErrorBox error={listQ.error} onRetry={() => listQ.refetch()} /></div>;
  else if (!listQ.data) table = <div className="card-b"><Skeleton /></div>;
  else {
    const { items, total } = listQ.data;
    const pc = Math.max(1, Math.ceil(total / PER_PAGE));
    const pg = Math.min(page, pc);
    const allOn = items.length > 0 && items.every((e) => sel.includes(e.id));
    table = items.length ? (
      <>
        <div className="tw">
          <table className="tbl cards" data-testid="employees-table">
            <thead>
              <tr>
                <th className="selc" style={{ width: 44 }}><input type="checkbox" aria-label={L("Select all", "تحديد الكل")} checked={allOn} onChange={() => setSel(allOn ? sel.filter((x) => !items.some((e) => e.id === x)) : [...new Set([...sel, ...items.map((e) => e.id)])])} data-testid="sel-all" /></th>
                {th("name", t("Employee"))}
                {th("dept", t("Department"), "hide-md")}
                <th className="hide-md">{t("Branch")}</th>
                <th>{t("Nationality")}</th>
                {th("joined", t("Joined"), "hide-md")}
                {th("basic", t("Basic salary"), "end")}
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => {
                const on = sel.includes(e.id);
                const iq = e.iqamaExpiry ? daysTo(today, e.iqamaExpiry) : null;
                const isOpen = open.includes(e.id);
                return (
                  <tr key={e.id} className={"click" + (on ? " sel" : "") + (isOpen ? " open" : "")} onClick={() => nav("/employees/" + e.id)} data-testid={"emp-row-" + e.id}>
                    <td className="selc" onClick={(ev) => ev.stopPropagation()}><input type="checkbox" aria-label={L("Select ", "تحديد ") + i.name(e)} checked={on} onChange={() => setSel(on ? sel.filter((x) => x !== e.id) : [...sel, e.id])} data-testid={"sel-" + e.id} /></td>
                    <td className="k1"><Person e={e} /></td>
                    <td className="hide-md x" data-l={t("Department")}>{i.dept(e.dept)}</td>
                    <td className="hide-md x" data-l={t("Branch")}>{i.branch(e.branch)}</td>
                    <td className="k3" data-l={t("Nationality")}>
                      <span className="row nw" style={{ gap: 6 }}>
                        {e.nationality === "SA" ? <Chip tone="pri" nodot>{t("Saudi")}</Chip> : i.nat(e.nationality)}
                        {iq != null && iq <= 30 && e.iqamaExpiry && <span title={t("Iqama")} data-testid={"iqama-chip-" + e.id}><ExpiryChip date={e.iqamaExpiry} today={today} /></span>}
                      </span>
                    </td>
                    <td className="hide-md x" data-l={t("Joined")}><DualDate date={e.joined} /></td>
                    <td className="end x" data-l={t("Basic salary")}><Money value={e.basic} /></td>
                    <td className="k2"><Chip status={e.status} /></td>
                    <td className="more-btn" onClick={(ev) => ev.stopPropagation()}>
                      <button type="button" className="btn ghost sm" onClick={() => setOpen(isOpen ? open.filter((x) => x !== e.id) : [...open, e.id])} aria-expanded={isOpen}>{isOpen ? t("Show less") : t("Show more")}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pager" data-testid="emp-pager">
          <span>{L("Showing " + ((pg - 1) * PER_PAGE + 1) + "–" + Math.min(pg * PER_PAGE, total) + " of " + total, "عرض " + ((pg - 1) * PER_PAGE + 1) + "–" + Math.min(pg * PER_PAGE, total) + " من " + total)}</span>
          <div className="row">
            <Button variant="sm" icon="chevron-left" aria-label={L("Previous page", "الصفحة السابقة")} disabled={pg <= 1} onClick={() => setPage(pg - 1)} data-testid="page-prev" />
            <span className="num">{pg} / {pc}</span>
            <Button variant="sm" icon="chevron-right" aria-label={L("Next page", "الصفحة التالية")} disabled={pg >= pc} onClick={() => setPage(pg + 1)} data-testid="page-next" />
          </div>
        </div>
      </>
    ) : (
      <EmptyState title={t("No employees found")} desc={t("Try a different name, or clear the filters.")} action={<Button variant="sm" onClick={clear}>{t("Clear filters")}</Button>} />
    );
  }

  return (
    <div data-testid="employees">
      <PageHeader
        title={t("Employees")}
        sub={allQ.data ? act.length + " " + L("employees", "موظفًا") + " · " + pct.toFixed(1) + "% " + t("Saudi") + " · " + branches + " " + t("Branches") : undefined}
        actions={<>
          {hr && <Button icon="upload" onClick={() => setImporting(true)} data-testid="btn-import">{t("Import")}</Button>}
          <Button icon="download" onClick={exportCsv} disabled={!allQ.data} data-testid="btn-export">{t("Export")}</Button>
          <Button icon="printer" onClick={() => openPrint({ doc: "directory" })} data-testid="btn-print">{t("Print")}</Button>
          {hr && <Button variant="pri" icon="plus" onClick={() => nav("/employees/new")} data-testid="btn-add">{t("Add employee")}</Button>}
        </>}
      />
      <section className="card">
        {toolbar}
        {sel.length > 0 && (
          <div className="bulk" data-testid="bulk-bar">
            <span>{sel.length} {t("selected")}</span><span className="spacer" />
            <Button variant="sm" onClick={() => toast(L("Letters queued for " + sel.length + " employees", "تمت جدولة الخطابات لـ " + sel.length + " موظفين"))} data-testid="bulk-letter">{t("Send letter")}</Button>
            {hr && <Button variant="sm" onClick={() => toast(L("Branch change saved", "تم تغيير الفرع"))} data-testid="bulk-branch">{t("Change branch")}</Button>}
            <Button variant="ghost sm" onClick={() => setSel([])} data-testid="bulk-clear">{t("Clear")}</Button>
          </div>
        )}
        {table}
      </section>
      {importing && <AiImportModal onClose={() => setImporting(false)} />}
    </div>
  );
}
